// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { SuperadminService } from "@/services/superadmin";
import { AuditLogFilters } from "@/components/superadmin/audit-logs/AuditLogFilters";
import { AuditLogTable } from "@/components/superadmin/audit-logs/AuditLogTable";
import { AuditLogDetail } from "@/components/superadmin/audit-logs/AuditLogDetail";
import { ExportButton } from "@/components/superadmin/audit-logs/ExportButton";
import type { AuditLogEntry } from "@/types/superadmin";

export interface SearchParams {
  page: number;
  action: string;
  targetType: string;
  actor: string;
  startDate: string;
  endDate: string;
  success: string;
}

export const Route = createFileRoute("/superadmin/audit-logs")({
  component: AuditLogsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    action: (search.action as string) || "",
    targetType: (search.targetType as string) || "",
    actor: (search.actor as string) || "",
    startDate: (search.startDate as string) || "",
    endDate: (search.endDate as string) || "",
    success: (search.success as string) || "all",
  }),
});

function AuditLogsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const params = Route.useSearch();

  // Debounced actor search
  const [actorInput, setActorInput] = useState(params.actor);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: "/superadmin/audit-logs",
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (actorInput === params.actor) {
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      updateParams({ actor: actorInput, page: 1 });
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [actorInput, params.actor, updateParams]);

  useEffect(() => {
    setActorInput(params.actor);
  }, [params.actor]);

  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "auditLogs", params],
    queryFn: () =>
      SuperadminService.getAuditLogs({
        page: params.page,
        limit: 20,
        action: params.action || undefined,
        targetType: params.targetType || undefined,
        actorSearch: params.actor || undefined,
        startDate: params.startDate || undefined,
        endDate: params.endDate || undefined,
        success: params.success,
      }),
  });

  // Detail sheet state
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleViewDetail = useCallback((log: AuditLogEntry) => {
    setSelectedLogId(log.id);
    setDetailOpen(true);
  }, []);

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      updateParams({ [key]: value, page: 1 });
    },
    [updateParams],
  );

  const handleClearFilters = useCallback(() => {
    clearTimeout(debounceRef.current);
    setActorInput("");
    navigate({
      to: "/superadmin/audit-logs",
      search: {
        page: 1,
        action: "",
        targetType: "",
        actor: "",
        startDate: "",
        endDate: "",
        success: "all",
      },
      replace: true,
    });
  }, [navigate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("auditLogs.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("auditLogs.description")}
          </p>
        </div>
        <ExportButton
          filters={{
            action: params.action || undefined,
            targetType: params.targetType || undefined,
            actorSearch: params.actor || undefined,
            startDate: params.startDate || undefined,
            endDate: params.endDate || undefined,
            success: params.success,
          }}
        />
      </div>

      <AuditLogFilters
        actorSearch={actorInput}
        onActorSearchChange={setActorInput}
        action={params.action}
        targetType={params.targetType}
        success={params.success}
        startDate={params.startDate}
        endDate={params.endDate}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      <AuditLogTable
        logs={data?.logs ?? []}
        pagination={data?.pagination}
        isLoading={isLoading}
        onPageChange={(page) => updateParams({ page })}
        onViewDetail={handleViewDetail}
      />

      <AuditLogDetail
        logId={selectedLogId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
}
